import React from "react";
import styled, { css } from "styled-components";

import * as colors from "../styles/colors";
import Arrow from "../images/icons/right-arrow.inline.svg";

const buttonSizesStyle = {
  compact: css`
    padding: 0 20px;
    font-size: 19px;
    min-width: 100px;
    min-height: 42px;

    @media (max-width: 768px) {
      padding: 0 16px;
      font-size: 16px;
      min-width: 60px;
      min-height: 30px;
      line-height: 100%;
    }
  `,
  regular: css`
    padding: 0 22px;
    font-size: 21px;
    min-width: 110px;
    min-height: 42px;
  `
};

const moreArrowStyle = css`
  .more {
    position: relative;
    left: 6px;
    margin-top: -2px;
    transition: all 0.3s;
  }
  &:hover .more {
    transition: all 0.3s;
    left: 10px;
  }
`;

const accentButtonStyles = css`
  font-weight: 400;
  border: 0;
  box-shadow: none;
  background-color: ${({ $accentBackgroundColor }) =>
    $accentBackgroundColor ?? colors.accent};
  color: ${({ $accentTextColor }) => $accentTextColor ?? colors.light};
  }
  &:hover {
    box-shadow: none;
    background-color: ${({ $accentBackgroundColor }) =>
      $accentBackgroundColor ?? "rgba(239, 81, 60, 1)"};
  }
  &:focus {
    outline: none;
    box-shadow: none !important;
    background-color: ${({ $accentBackgroundColor }) =>
      $accentBackgroundColor ?? "rgba(239, 81, 60, 1)"};
  }
  &:active {
    outline: none;
    box-shadow: none;
    background-color: ${({ $accentBackgroundColor }) =>
      $accentBackgroundColor ?? "rgba(239, 81, 60, 1)"};
  }
  &.loading {
    outline: none;
    color: ${({ $accentTextColor }) =>
      $accentTextColor ?? colors.light};
    background-color: transparent;
    box-shadow: 0 0 0px 3px ${({ $accentBackgroundColor }) =>
      $accentBackgroundColor ?? colors.accent};
  }
`;

const primaryButtonStyles = css`
  ${moreArrowStyle}
  font-family: "Eina02-SemiBold", sans-serif !important;
  padding: 12px 24px;
  font-weight: 400;
  font-size: 18px;
  border-radius: 8px;
  border: 0;
  box-shadow: none;
  background-color: ${({ $accentBackgroundColor }) =>
    $accentBackgroundColor ?? colors.primaryButtonColor};
  color: ${({ $accentTextColor }) =>
    $accentTextColor ?? colors.whiteLightColor};
  &:hover {
    box-shadow: 0px 0px 15px
      ${({ $accentBackgroundColor }) =>
        $accentBackgroundColor ?? "rgba(255, 88, 65, 0.8)"};
    color: ${({ $accentTextColor }) =>
      $accentTextColor ?? colors.whiteLightColor};
    transition: box-shadow 0.2s ease-out;
  }
  &:focus,
  &:active,
  &.loading {
    outline: none;
    color: ${({ $accentTextColor }) =>
      $accentTextColor ?? colors.light};
    background-color: ${({ $accentBackgroundColor }) =>
      $accentBackgroundColor ?? "#ef513c"};
    box-shadow: none;
  }
`;

export const secondaryButtonStyles = css`
  ${moreArrowStyle}
  padding: 12px 24px;
  font-weight: 400;
  font-size: 18px;
  border-radius: 8px;
  border: 1px solid transparent;
  box-shadow: none;
  display: flex;
  text-decoration: none;
  width: fit-content;
  border: 1px solid rgba(106, 209, 228, 0.32);
  span {
    background: radial-gradient(
      117.29% 1278% at 107.91% 94.25%,
      #0068c6 0%,
      #82fcff 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    -webkit-box-decoration-break: clone;
    text-shadow: 0px 0px #00000000;
  }
  &:hover {
    transition: all 0.3s ease-out;
    border: 1px solid transparent;
    -webkit-text-fill-color: unset;
    background: ${colors.backgroundNavItem};
  }
  &:hover span {
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-color: ${colors.primaryGreen};
    animation: stripes 0.3s ease-in 1 forwards;
    -webkit-text-fill-color: transparent;
  }
  &:focus {
    box-shadow: none;
    text-decoration: none;
  }
  @keyframes stripes {
    to {
      background-size: 0% 100%;
    }
  }
  // &:active {
  //   box-shadow: none;
  //   text-decoration: none;
  //   background: radial-gradient(
  //     117.29% 1278% at 107.91% 94.25%,
  //     #0068c6 0%,
  //     #82fcff 100%
  //   );
  //   -webkit-background-clip: text;
  //   -webkit-text-fill-color: transparent;
  // }
`;

export const tertiaryButtonStyles = css`
  ${moreArrowStyle}
  font-family: "Eina02-SemiBold", sans-serif !important;

  padding: 12px 24px;
  font-weight: 400;
  font-size: 18px;
  border-radius: 8px;
  border: 0;
  box-shadow: none;
  color: black;
  background: ${colors.ctaGradientBlue};
  &:hover {
    box-shadow: 0px 0px 12px rgba(83, 192, 232, 0.8);
    transition: box-shadow 0.2s ease-out;
  }
  &:focus {
    color: black;
    background: ${colors.ctaGradientBlueFocus};
  }
  &.loading {
    outline: none;
    color: ${colors.light};
    background-color: #ef513c;
    box-shadow: none;
  }
`;

export const TextInBtn = styled.div`
  display: flex;
`;

const getButtonSize = ({ $compact }) => {
  if ($compact) {
    return buttonSizesStyle.compact;
  }

  return buttonSizesStyle.regular;
};

export const buttonStyles = css`
  ${({ $compact }) => getButtonSize({ $compact })}
  font-family: "Eina02-Regular", sans-serif;
  margin: 0;
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  border: none;
  border-radius: 20px;
  box-shadow: 0 0 0px 2px
    ${({ disabled }) =>
      disabled ? colors.muted : colors.primaryBright};
  color: ${({ disabled }) =>
    disabled ? colors.muted : colors.primaryBright};
  text-align: center;
  justify-content: center;
  transition: all 0.2s;
  text-decoration: none;
  background: transparent;
  cursor: ${({ disabled }) =>
    disabled ? "not-allowed" : "pointer"} !important;
  position: relative;
  box-sizing: border-box;
  opacity: ${({ disabled }) => (disabled ? "0.7" : "1")};
  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px ${colors.primaryBright};
  }
`;

const bottomFixedStyles = css`
  position: fixed;
  bottom: 24px;
  z-index: 5000;
  width: 90%;
  max-width: 400px;
  left: 50%;
  transform: translateX(-50%);
`;

const navbarItemBaseStyles = css`
  height: 42px;
  margin: 0 8px;
  padding: 0px 20px;
  font-weight: 300;
  border-radius: 8px;
  box-shadow: none;
  min-width: auto;

  span {
    font-size: 18px;
    background: #6ad1e4;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    -webkit-box-decoration-break: clone;
    text-shadow: 0px 0px #00000000;
  }
  &:hover {
    background: ${colors.backgroundNavItem};
  }
  &:hover span {
    background-size: 0% 100%;
    background-repeat: no-repeat;
    animation: stripes 0.3s ease-in-out 1 forwards;
    -webkit-text-fill-color: transparent;
    background-image: ${colors.blueGradient}
    color: #fff;
    text-decoration: none;
    -webkit-background-clip: text;
    background-clip: text;
  }
  @keyframes stripes {
    to {
      background-size: 100% 100%;
    }
  }
  &.active {
    background: ${colors.backgroundNavItem};
    span {
      color: ${colors.primary};
    }
  }
  &:focus {
    transition: all 2s ease-out;
    box-shadow: none;
    background: ${colors.backgroundNavItem};
    span {
      background: radial-gradient(
        117.29% 1278% at 107.91% 94.25%,
        #0068c6 0%,
        #82fcff 100%
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      -moz-background-clip: text;
      -moz-text-fill-color: transparent;
    }
  }
`;

const StyledButton = styled.button`
  ${buttonStyles};
  ${({ $accent }) => $accent && accentButtonStyles}
  ${({ $primaryButton }) => $primaryButton && primaryButtonStyles}
  ${({ $secondaryButton }) => $secondaryButton && secondaryButtonStyles}
  ${({ $tertiaryButton }) => $tertiaryButton && tertiaryButtonStyles}
  ${({ $navItem }) => $navItem && navbarItemBaseStyles}
  ${({ $fixed }) => $fixed && bottomFixedStyles}
  cursor: pointer;
`;

export function Button({
  children,
  accent = false,
  compact = false,
  loading = false,
  primaryButton = false,
  secondaryButton = false,
  tertiaryButton = false,
  navItem = false,
  fixed = false,
  customLoadingClass = "",
  more = false,
  arrowColor = colors.dimmedLight,
  accentBackgroundColor = null,
  accentTextColor = null,
  ...props
}) {
  return (
    <StyledButton
      {...props}
      $compact={compact}
      $accent={accent}
      $accentBackgroundColor={accentBackgroundColor}
      $accentTextColor={accentTextColor}
      $primaryButton={primaryButton}
      $secondaryButton={secondaryButton}
      $tertiaryButton={tertiaryButton}
      $navItem={navItem}
      $fixed={fixed}
      className={`${loading ? "loading" : ""}`}
    >
      <TextInBtn>
        {loading ? (
          <div
            className={`spinner ${
              accent ? "spinner-accent" : ""
            } ${customLoadingClass}`}
          />
        ) : (
          <>
            {children}
            {more ? (
              <Arrow
                className="more"
                fill={
                  secondaryButton || tertiaryButton
                    ? colors.primaryGreen
                    : arrowColor
                }
                alt="Arrow"
              />
            ) : null}
          </>
        )}
      </TextInBtn>
    </StyledButton>
  );
}
